import { useState, useCallback } from 'react';
import { api } from '../api/client';

interface UploadedFile {
  id: string;
  fileName: string;
  originalName: string;
  mimeType: string;
  size: number;
  url: string;
  thumbnailUrl?: string;
  createdAt: string;
}

interface UseFileUploadOptions {
  entityType: 'request' | 'construction' | 'board' | 'talkroom' | 'daily-report' | 'user' | 'company';
  entityId?: string;
  onSuccess?: (files: UploadedFile[]) => void;
  onError?: (error: Error) => void;
}

interface UseFileUploadReturn {
  files: UploadedFile[];
  isUploading: boolean;
  isLoading: boolean;
  error: string | null;
  uploadFiles: (files: File[]) => Promise<string[]>;
  deleteFile: (fileId: string) => Promise<void>;
  fetchFiles: () => Promise<void>;
  clearError: () => void;
}

export function useFileUpload({
  entityType,
  entityId,
  onSuccess,
  onError,
}: UseFileUploadOptions): UseFileUploadReturn {
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const fetchFiles = useCallback(async () => {
    if (!entityId) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await api.get<{ items: UploadedFile[] }>(
        `/api/files?entityType=${entityType}&entityId=${entityId}`
      );
      // responseがnull/undefinedの場合も安全に処理
      setFiles(Array.isArray(response?.items) ? response.items : []);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'ファイルの取得に失敗しました';
      setError(message);
      console.error('Failed to fetch files:', err);
    } finally {
      setIsLoading(false);
    }
  }, [entityType, entityId]);

  const uploadFiles = useCallback(async (filesToUpload: File[]): Promise<string[]> => {
    if (filesToUpload.length === 0) return [];

    setIsUploading(true);
    setError(null);

    try {
      const uploadPromises = filesToUpload.map(async (file) => {
        // FormData を使ってファイルをアップロード
        const formData = new FormData();
        formData.append('file', file);
        formData.append('entityType', entityType);
        if (entityId) {
          formData.append('entityId', entityId);
        }

        const response = await api.uploadFile(file, {
          entityType,
          entityId,
        });

        return response;
      });

      const urls = await Promise.all(uploadPromises);

      // ファイル一覧を更新
      if (entityId) {
        await fetchFiles();
      }

      if (onSuccess) {
        onSuccess(files);
      }

      return urls;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'アップロードに失敗しました';
      setError(message);
      console.error('Failed to upload files:', err);

      if (onError) {
        onError(err instanceof Error ? err : new Error(message));
      }

      throw err;
    } finally {
      setIsUploading(false);
    }
  }, [entityType, entityId, fetchFiles, files, onSuccess, onError]);

  const deleteFile = useCallback(async (fileId: string) => {
    setError(null);

    try {
      await api.delete(`/api/files/${fileId}`);
      setFiles(prev => prev.filter(f => f.id !== fileId));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'ファイルの削除に失敗しました';
      setError(message);
      console.error('Failed to delete file:', err);
      throw err;
    }
  }, []);

  return {
    files,
    isUploading,
    isLoading,
    error,
    uploadFiles,
    deleteFile,
    fetchFiles,
    clearError,
  };
}

// ファイルプレビュー用のユーティリティ
export function getFilePreviewUrl(file: UploadedFile): string | null {
  if (file.thumbnailUrl) {
    return file.thumbnailUrl;
  }

  // 画像ファイルの場合はURLをそのまま返す
  if (file.mimeType.startsWith('image/')) {
    return file.url;
  }

  return null;
}

// ファイル種別判定
export function getFileCategory(mimeType: string): 'image' | 'document' | 'spreadsheet' | 'pdf' | 'other' {
  if (mimeType.startsWith('image/')) return 'image';
  if (mimeType === 'application/pdf') return 'pdf';
  if (
    mimeType.includes('spreadsheet') ||
    mimeType.includes('excel') ||
    mimeType === 'application/vnd.ms-excel' ||
    mimeType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  ) {
    return 'spreadsheet';
  }
  if (
    mimeType.includes('document') ||
    mimeType.includes('word') ||
    mimeType === 'application/msword' ||
    mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  ) {
    return 'document';
  }
  return 'other';
}

// ダウンロード処理
export async function downloadFile(url: string, fileName: string): Promise<void> {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
  } catch (error) {
    console.error('Download failed:', error);
    // フォールバック: 新しいタブで開く
    window.open(url, '_blank');
  }
}
